import { type ValidationErrors } from '@angular/forms';

import { type IDataBinding } from '../form.component';

interface AngularLengthError {
  requiredLength: number;
  actualLength: number;
}

/** Payloads of the Angular built-in validators, keyed by the name Angular reports them under. */
interface AngularValidatorErrors {
  required: true;
  minlength: AngularLengthError;
  maxlength: AngularLengthError;
}

/** Bindings of the `_system` wordings, mirroring their `{{ }}` placeholders in `system.ts`. */
interface SystemWordingBindings {
  errorRequiredField: undefined;
  errorMinLength: { minLength: number; length: number };
  errorMaxLength: { maxLength: number; length: number };
}

function systemMessage<K extends keyof SystemWordingBindings>(
  key: K,
  binding: SystemWordingBindings[K],
): IDataBinding {
  return { binding, key: `_system.${key}` };
}

const ANGULAR_VALIDATOR_MESSAGES: {
  [K in keyof AngularValidatorErrors]: (
    error: AngularValidatorErrors[K],
  ) => IDataBinding;
} = {
  required: () => systemMessage('errorRequiredField', undefined),
  minlength: ({ requiredLength, actualLength }) =>
    systemMessage('errorMinLength', {
      minLength: requiredLength,
      length: actualLength,
    }),
  maxlength: ({ requiredLength, actualLength }) =>
    systemMessage('errorMaxLength', {
      maxLength: requiredLength,
      length: actualLength,
    }),
};

function isAngularValidatorKey(
  key: string,
): key is keyof AngularValidatorErrors {
  return Object.hasOwn(ANGULAR_VALIDATOR_MESSAGES, key);
}

/** The other validators key their errors by the wording to display. */
export function toValidationMessage(
  key: string,
  error: ValidationErrors[string],
): IDataBinding {
  return isAngularValidatorKey(key)
    ? ANGULAR_VALIDATOR_MESSAGES[key](error)
    : { key, binding: error };
}
